import React, { useState, useEffect } from 'react';
import './App.css';
import { useNavigate } from 'react-router-dom';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import ProductDetail from './components/ProductDetail';
import Shop from './components/Shop';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AboutUs from './components/AboutUs';
import Dashboard from './components/Dashboard';
import Points from './components/Points';
import EditProfile from './components/EditProfile';
import CategoryProductList from './components/CategoryProductList';
import Homepage from './components/Homepage';
import ContactUs from './components/ContactUs';
import SearchResults from './components/SearchResults';
import PublicReviews from './components/PublicReview';
import WriteReview from './components/WriteReview';
import AdminImageManager from './components/AdminImageManager';
import AdminProductList from './components/AdminProductList';
import AdminOrderList from './components/AdminOrderList';
import AdminCategoryList from './components/AdminCategoryList';
import AdminEditProduct from './components/AdminEditProduct';
import AdminEditRedeemableProduct from './components/AdminEditRedeemableProduct';
import AdminAddCategory from './components/AdminAddCategory';
import AdminEditCategory from './components/AdminEditCategory';
import AdminAddProduct from './components/AdminAddProduct';
import AdminAddRedeemableProduct from './components/AdminAddRedeemableProduct';
import AdminOrderDetail from './components/AdminOrderDetail';
import AdminDeletedProductList from './components/AdminDeletedProductList';
import AdminDeletedRedeemableProductList from './components/AdminDeletedRedeemableProductList';
import AdminRedeemableProductList from './components/AdminRedeemableProductList';
import AdminReviewManagement from './components/AdminReviewManagement';
import { CartProvider } from './CartContext';
import { AuthProvider } from './AuthContext';
import { LoadingProvider } from './LoadingContext';
import Loading from './components/Loading';
import InquiryForm from './components/InquiryForm';
import AdminInquiries from './components/AdminInquiries';
import AdminVouchers from './components/AdminVoucher';
import AdminVoucherRedeem from './components/AdminVoucherRedeem';
import TermsAndConditions from './components/TermsAndConditions';
import Catalogue from './components/Catalogue';
import AdminEditProductVariant from './components/AdminEditProductVariant';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminSalesAnalytics from './components/AdminSalesAnalytics';
import AdminVoucherAnalytics from './components/AdminVoucherAnalytics';
import AdminCatalogue from './components/AdminCatalogue';
import AdminManagement from './components/AdminManagement';
import AdminCustomerVisit from './components/AdminCustomerVisit';
import AdminProductView from './components/AdminProductView';
import AdminPromotion from './components/AdminPromotion';
import AdminAddPromotionProduct from './components/AdminAddPromotionProduct';
import AdminEditPromotionProduct from './components/AdminEditPromotionProduct';
import AdminCustomer from './components/AdminCustomer';
import AdminContactEnquiries from './components/AdminContactEnquiries';
import AdminHomepagePopup from './components/AdminHomepagePopup';
import Promotion from './components/Promotion';
import PromotionDetail from './components/PromotionDetail';

import { useContext } from 'react';
import { AuthContext } from './AuthContext';   

const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <>
      {!isAdminPage && <Header />}
      <Loading />
      <div className="container">
        {children}
      </div>
      {!isAdminPage && <Footer />}
      {!isAdminPage && <WhatsAppButton />}
    </>
  );
};

const AdminRouteGuard = ({ element, requiredFeature, requireFullAdmin }) => {
  const { isLoggedIn, userInfo, loading, hasAccess, isAdmin, isAnyAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [redirected, setRedirected] = useState(false);

  useEffect(() => {
    const checkAuthorization = async () => {
      if (loading) return;

      if (!isLoggedIn || !userInfo) {
        setAuthorized(false);
        setCheckingAuth(false);
        return;
      }

      try {
        if (requireFullAdmin) {
          const isUserAdmin = await isAdmin();
          setAuthorized(isUserAdmin);
          setCheckingAuth(false);
          return;
        }

        if (requiredFeature) {
          const isUserAdmin = await isAdmin();
          if (isUserAdmin) {
            setAuthorized(true);
            setCheckingAuth(false);
            return;
          }
          
          const hasFeatureAccess = await hasAccess(requiredFeature);
          setAuthorized(hasFeatureAccess);
          setCheckingAuth(false);
          return;
        }

        const isUserAnyAdmin = await isAnyAdmin();
        setAuthorized(isUserAnyAdmin);
        setCheckingAuth(false);
      } catch (error) {
        setAuthorized(false);
        setCheckingAuth(false);
      }
    };

    checkAuthorization();
  }, [loading, isLoggedIn, userInfo, requireFullAdmin, requiredFeature, isAdmin, hasAccess, isAnyAdmin]);

  useEffect(() => {
    const handleUnauthorizedRedirect = () => {
      if (checkingAuth || loading || authorized || redirected) return;

      setRedirected(true);

      if (!isLoggedIn) {
        navigate('/admin/login', { replace: true });
      } else {
        navigate("/login", {
          replace: true
        });
      }
    };

    handleUnauthorizedRedirect();
  }, [authorized, checkingAuth, isLoggedIn, loading, navigate, redirected]);

  if (loading || checkingAuth) {
    return <div className="admin-loading-container">
      <div className="admin-loading-spinner"></div>
      <div>Verifying authentication...</div>
    </div>;
  }

  if (!authorized) {
    return (
      <div className="admin-loading-container">
        <div className="admin-loading-spinner"></div>
        <div>Redirecting...</div>
      </div>
    );
  }

  return element;
};

function App() {
  return (
    <LoadingProvider>
      <AuthProvider>
        <CartProvider>
          <Router>
            <div className="App">
              <Routes>
                <Route path="/*" element={
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Homepage />} />
                      <Route path="/category" element={<Shop />} />
                      <Route path="/product/:productCode" element={<ProductDetail />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/points" element={<Points />} />
                      <Route path="/profile/edit" element={<EditProfile />} />
                      <Route path="/category/:categoryID" element={<CategoryProductList />} />
                      <Route path="/product/:productCode/inquiry" element={<InquiryForm />} />
                      <Route path="/contact" element={<ContactUs />} />
                      <Route path="/terms" element={<TermsAndConditions />} />
                      <Route path="/catalogue" element={<Catalogue />} />
                      <Route path="/promotion" element={<Promotion />} />
                      <Route path="/promotion/:id" element={<PromotionDetail />} />
                      <Route path="/search" element={<SearchResults />} />
                      <Route path="/review" element={<PublicReviews />} />
                      <Route path="/dashboard/write-review" element={<WriteReview />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Layout>
                } />
                <Route path="/admin/*" element={
                  <div className="App">
                    <Loading />
                    <div className="container">
                      <Routes>
                        <Route path="/login" element={<AdminLogin />} />

                        <Route path="/dashboard" element={<AdminRouteGuard element={<AdminDashboard />} />
                        } />

                        <Route path="/edit-product/:id" element={
                          <AdminRouteGuard element={<AdminEditProduct />} requiredFeature="products" />
                        } />
                        <Route path="/edit-product/redeemable/:id" element={
                          <AdminRouteGuard element={<AdminEditRedeemableProduct />} requiredFeature="products" />
                        } />
                        <Route path="/products/edit/:id/variants" element={
                          <AdminRouteGuard element={<AdminEditProductVariant />} requiredFeature="variants" />
                        } />
                        <Route path="/category" element={
                          <AdminRouteGuard element={<AdminCategoryList />} requiredFeature="categories" />
                        } />
                        <Route path="/category/add" element={
                          <AdminRouteGuard element={<AdminAddCategory />} requiredFeature="categories" />
                        } />
                        {<Route path="/category/edit/:id" element={
                          <AdminRouteGuard element={<AdminEditCategory />} requiredFeature="categories" />
                        } />}
                        <Route path="/products" element={
                          <AdminRouteGuard element={<AdminProductList />} requiredFeature="products" />
                        } />
                        <Route path="/products/redeemable" element={
                          <AdminRouteGuard element={<AdminRedeemableProductList />} requiredFeature="products" />
                        } />
                        <Route path="/add-product" element={
                          <AdminRouteGuard element={<AdminAddProduct />} requiredFeature="products" />
                        } />
                        <Route path="/add-product/redeemable" element={
                          <AdminRouteGuard element={<AdminAddRedeemableProduct />} requiredFeature="products" />
                        } />
                        <Route path="/deleted-products/selling" element={
                          <AdminRouteGuard element={<AdminDeletedProductList />} requiredFeature="products" />
                        } />
                        <Route path="/deleted-products/redeemable" element={
                          <AdminRouteGuard element={<AdminDeletedRedeemableProductList />} requiredFeature="products" />
                        } />
                        <Route path="/orders" element={
                          <AdminRouteGuard element={<AdminOrderList />} requiredFeature="orders" />
                        } />
                        <Route path="/orders/:orderId" element={
                          <AdminRouteGuard element={<AdminOrderDetail />} requiredFeature="orders" />
                        } />
                        <Route path="/inquiries" element={
                          <AdminRouteGuard element={<AdminInquiries />} requiredFeature="inquiries" />
                        } />
                        <Route path="/vouchers" element={
                          <AdminRouteGuard element={<AdminVouchers />} requiredFeature="vouchers" />
                        } />
                        <Route path="/vouchers/redeemable" element={
                          <AdminRouteGuard element={<AdminVoucherRedeem />} requiredFeature="vouchers" />
                        } />

                        <Route path="/manage-admins" element={
                          <AdminRouteGuard element={<AdminManagement />} requireFullAdmin={true} />
                        } />
                        <Route path="/catalogue" element={
                          <AdminRouteGuard element={<AdminCatalogue />} requireFullAdmin={true} />
                        } />
                        <Route path="/images" element={
                          <AdminRouteGuard element={<AdminImageManager />} requireFullAdmin={true} />
                        } />
                        <Route path="/salesanalytics" element={
                          <AdminRouteGuard element={<AdminSalesAnalytics />} requireFullAdmin={true} />
                        } />
                        <Route path="/customervisit" element={
                          <AdminRouteGuard element={<AdminCustomerVisit />} requireFullAdmin={true} />
                        } />
                        <Route path="/productview" element={
                          <AdminRouteGuard element={<AdminProductView />} requireFullAdmin={true} />
                        } />
                        <Route path="/voucheranalytics" element={
                          <AdminRouteGuard element={<AdminVoucherAnalytics />} requireFullAdmin={true} />
                        } />
                        <Route path="/promotions" element={
                          <AdminRouteGuard element={<AdminPromotion />} requireFullAdmin={true} />
                        } />
                        <Route path="/promotions/add" element={
                          <AdminRouteGuard element={<AdminAddPromotionProduct />} requireFullAdmin={true} />
                        } />
                        <Route path="/promotions/edit/:id" element={
                          <AdminRouteGuard element={<AdminEditPromotionProduct />} requireFullAdmin={true} />
                        } />
                        <Route path="/review" element={
                          <AdminRouteGuard element={<AdminReviewManagement />} requireFullAdmin={true} />
                        } />
                        <Route path="/customers" element={
                          <AdminRouteGuard element={<AdminCustomer />} requireFullAdmin={true} />
                        } />
                        <Route path="/contact-enquiries" element={
                          <AdminRouteGuard element={<AdminContactEnquiries />} requiredFeature="contactEnquiries" />
                        } />
                        <Route path="/homepage-popup" element={
                          <AdminRouteGuard element={<AdminHomepagePopup />} requireFullAdmin={true} />
                        } />

                        {<Route path="*" element={<Navigate to="/admin/dashboard" replace />} />}
                      </Routes>
                    </div>
                  </div>
                } />
              </Routes>
            </div>
          </Router>
        </CartProvider>
      </AuthProvider>
    </LoadingProvider>
  );
}

export default App;